import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, animate, motion } from 'framer-motion';
import subjectHeroReading from './assets/subject-hero-reading.png';
import subjectHeroMath from './assets/subject-hero-math.png';
import { DOMAIN_TOOL_ART } from './assets/domainArt';
import {
  DOMAINS, subjectLabel, STUDENT, progressOf, partsFor,
  MAX_LEVEL, minLevelOf, unitDue,
} from './data/studentMock';
import { useAuth } from '../Accounts/AuthContext';
import { fetchAllWalks, hasPlacement } from './data/placementApi';
import { isOpenAssignmentSlot, isUnknownAssignmentSlot } from './data/assignmentSlots';
import { dueDateOf, dueEndMs, shortDue } from '../TeacherDashboardV3/data/unitPlan';
import './sdv3.css';
import { bandRange } from '../utils/satScoreBands';

/*
 * SubjectPanel — the subject dashboard, now a TAB on the student home
 * (Sandy 2026-08-12): the home page owns the data fetches and the tab bar;
 * this panel takes `real` (getMyProgress) and `agenda` (useStudentAgenda) as
 * props and renders the working surface: a compact expected-score lockup,
 * Buffy's one "because" line, THE PATH (four domain cards in working order),
 * and the full ladders below. Remounted per subject via key={subject}.
 *
 * The old /student-dashboard/:subject route now redirects into the tab
 * (default export below) so every existing link keeps working.
 *
 * Path order: real class units are pinned to the front (earliest due first);
 * the rest sort by lowest level first, tie broken by score weight. Each card:
 * level eyebrow, the domain's tool art, progress bar or Level-up footer.
 * Sized for Chromebooks (1366×768).
 */

const SUBJECT_HERO = { reading: subjectHeroReading, math: subjectHeroMath };

// One color per domain, drawn from the subject palette; rim = darker same hue;
// grad = the FIXED gradient slice its Level-up pill wears (warm slices for
// reading, purple for expression, blue slices for math — never the rainbow).
const DOMAIN_COLORS = {
  // Reading & Writing: the reds and golds from the tool art
  info: { c: '#E03A3A', rim: '#B02A2A', text: '#ffffff', grad: 'linear-gradient(115deg, #d44995, #ff333d)' },
  craft: { c: '#F8C12A', rim: '#C29008', text: '#141414', grad: 'linear-gradient(115deg, #ff333d, #ff8140, #ffcb57)' },
  conventions: { c: '#C83030', rim: '#9A2424', text: '#ffffff', grad: 'linear-gradient(115deg, #C83030, #ff8140)' },
  expression: { c: '#F8D868', rim: '#CFA92F', text: '#141414', grad: 'linear-gradient(115deg, #a960ee, #d44995)' },
  // Math: blues
  algebra: { c: '#2563EB', rim: '#1E40AF', text: '#ffffff', grad: 'linear-gradient(115deg, #1E40AF, #3B82F6)' },
  advanced: { c: '#3B82F6', rim: '#1D4ED8', text: '#ffffff', grad: 'linear-gradient(115deg, #2563EB, #60A5FA)' },
  psda: { c: '#60A5FA', rim: '#2563EB', text: '#141414', grad: 'linear-gradient(115deg, #3B82F6, #90e0ff)' },
  geotrig: { c: '#1E40AF', rim: '#16308C', text: '#ffffff', grad: 'linear-gradient(115deg, #16308C, #2563EB)' },
};
const FALLBACK_COLOR = { c: '#2563EB', rim: '#1E40AF', text: '#ffffff', grad: 'linear-gradient(115deg, #1E40AF, #3B82F6)' };

// Section-score points a domain level-up is worth (weight × ~65-point band
// gap, rounded to the SAT's 10-point scale so the displayed score only ever
// moves in increments of ten). Math weights use the same method: Algebra and
// Advanced Math are each ~35% of the section's operational questions, PSDA
// and Geometry/Trig ~15% each.
const DOMAIN_POINTS = {
  craft: 20, info: 20, conventions: 20, expression: 10,
  algebra: 20, advanced: 20, psda: 10, geotrig: 10,
};

// Mock eligibility: which domains have a passed skill check waiting on the click.
// Only the ACTIVE domain can be ready — you can't finish a check where you
// aren't working.
const READY_DEFAULT = { expression: true, craft: false, info: false, conventions: false };

// Optical nudges for card art whose visual mass sits off its canvas center
// (the key leans right). CSS transform, not asset edits.
const ART_NUDGE = { craft: 'translateX(-3px)' };

// Counts from -> to for the level-up score equation. SAT scores move in tens,
// so the tick does too. Starts after `delay` seconds so the equation can
// assemble first. (Counting from 0 flashed nonsense like "400 + 10 = 130"
// mid-flight — QA 2026-08-27; the equation's right side must never dip below
// its left side.)
function Ticker({ from = 0, to, delay, duration, onDone }) {
  const [v, setV] = useState(from);
  useEffect(() => {
    const controls = animate(from, to, {
      delay,
      duration,
      ease: 'easeOut',
      onUpdate: (x) => setV(Math.round(x / 10) * 10),
      onComplete: onDone,
    });
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to]);
  return <>{v}</>;
}

// One stop on the path — a Brilliant-style course card. Every placed stop
// shows its level eyebrow (the ladders are independent, so the numbers differ
// per card); only the ACTIVE stop carries working detail: the due date above
// it (ink, no timeline labels), the progress bar, or the full-width Level-up
// footer when its check is passed. A ready card's outline turns into the
// moving brand rainbow (graduation moment). The whole card opens the road.
function PathCard({ subject, domain, level, ready, pct, active, dueLabel, dueOverdue, onLevelUp, startable, placement }) {
  const navigate = useNavigate();
  const placed = level != null;
  // startable (real signed-in account): an unplaced domain is still enterable —
  // the domain page's Start launches the engine, which self-places the student.
  // Only the signed-out demo keeps unplaced cards dead (?unplaced=1 preview).
  // A domain with no level yet — math OR reading (reading pools added
  // 2026-08-27) — is entered through its own placement check
  // (claude-placement-api): 10-12 adaptive questions whose result now writes
  // the level into the engine profile.
  const placementUrl = placement ? `/placement/${domain.key}` : null;
  const enterable = placed || startable || !!placementUrl;
  const color = DOMAIN_COLORS[domain.key] || FALLBACK_COLOR;
  // Gray by default. The brand rainbow appears only when this is the one you
  // have to do (your class unit) or you are leveling up. No per-domain color.
  // An UNPLACED card with a live class unit rainbows too (Sandy 2026-08-30):
  // the placement walk is the unit's assignment 1, so "Find your level" is
  // the thing you have to do.
  const rainbow = active || (placed && ready);
  // Domains open IN PLACE now (Sandy 2026-08-12): the roadmap renders under
  // the home page's subject tabs, so the card sets the ?domain param there.
  const open = () => navigate(
    !placed && placementUrl
      ? placementUrl
      : `/student-dashboard?tab=${subject}&domain=${domain.key}${enterable ? '' : '&unplaced=1'}`,
  );
  return (
    <div className="relative z-10 flex flex-col items-center min-w-0">
      {/* spacer keeps the card baseline fixed so the connector line stays put;
          the "do this" tab is absolutely positioned on the card below. */}
      <div className="h-[14px] mb-2" aria-hidden="true" />

      <div
        role="button"
        tabIndex={enterable ? 0 : -1}
        onClick={enterable ? open : undefined}
        onKeyDown={(e) => { if (enterable && e.key === 'Enter') open(); }}
        className={'sdv3-key relative w-full max-w-[240px] h-[196px] rounded-[16px] bg-white p-3.5 flex flex-col'
          + (enterable ? ' cursor-pointer' : ' cursor-default')
          + (rainbow ? ' sdv3-grad-outline' : '')}
        style={{
          ...(rainbow ? {} : { border: '2px solid #E5E5E5' }),
          '--sdv3-btn-rim': '#d4d4d4',
          // Not placed yet: grayed out — but a real account's card stays fully
          // visible and tappable, since entering is how placement happens.
          ...(placed || startable || placementUrl ? {} : { opacity: 0.55, filter: 'grayscale(1)' }),
        }}
      >
        {/* the "you have to do this" tab, popping out of the top of the card */}
        {dueLabel && (
          <span className={'absolute left-1/2 -translate-x-1/2 -top-[14px] z-20 inline-flex items-center px-[10px] py-[3px] rounded-[8px] text-white text-[10px] font-extrabold uppercase tracking-wide whitespace-nowrap shadow-[0_3px_10px_rgba(20,20,20,0.22)] '
            + (dueOverdue ? 'bg-error-600' : 'bg-ink')}>
            {dueLabel}
          </span>
        )}

        {/* level eyebrow: uniform gray on every card, active included.
            Unplaced cards with a walk put the action up here — "Find your
            level" / "Finish your check" — instead of a "Not placed yet"
            label (Sandy 2026-08-27: the label said nothing the action
            doesn't). "Not placed yet" survives only on the signed-out
            demo preview, where the card is dead and there is no action. */}
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#a3a3a3]">
            {placed ? `Level ${level}`
              : placement?.placedBand != null ? `Level ${placement.placedBand}`
                : placement?.status === 'active' ? 'Finish your check'
                  : placementUrl ? 'Find your level'
                    : 'Not placed yet'}
          </span>
        </div>

        <img
          src={DOMAIN_TOOL_ART[domain.key]}
          alt=""
          className="h-[96px] object-contain mx-auto my-auto"
          style={ART_NUDGE[domain.key] ? { transform: ART_NUDGE[domain.key] } : undefined}
        />

        {/* The 7 levels, as little boxes (Sandy 2026-08-12: the old "All your
            levels" ladders folded into the cards): blue = done, light blue =
            where you are, gray = still locked. Levels below the content floor
            (no Level 1 in info/craft/expression) don't render. */}
        {placed && (ready && active ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onLevelUp(domain.key); }}
            className="sdv3-shimmer w-full h-8 mt-1 shrink-0 rounded-[10px] border-none cursor-pointer flex items-center justify-center"
            style={{ background: color.grad }}
          >
            <span className="relative z-10 text-[13px] font-extrabold text-white leading-none" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.35)' }}>
              Level up
            </span>
          </button>
        ) : (
          <div className="w-full flex gap-1" title={`Level ${level} of ${MAX_LEVEL}`}>
            {Array.from({ length: MAX_LEVEL }, (_, i) => i + 1).filter((n) => n >= minLevelOf(domain.key)).map((n) => (
              <span
                key={n}
                className={
                  'h-3 flex-1 rounded-[4px] ' +
                  (n < level ? 'bg-primary-500' : n === level ? 'bg-primary-300' : 'bg-neutral-100')
                }
              />
            ))}
          </div>
        ))}
        {/* Bottom line only carries what the eyebrow doesn't: provenance for a
            finished check ("From your check" under its Level N eyebrow), or
            the no-walk fallback. Walk actions moved to the eyebrow. */}
        {!placed && (placementUrl
          ? (placement.placedBand != null && (
            <div className="text-[11px] font-bold text-neutral-400 text-center">From your check</div>
          ))
          : (
            <div className="text-[11px] font-bold text-neutral-400 text-center">
              {startable ? 'Tap to start' : 'Not placed yet'}
            </div>
          ))}
      </div>

      <div className="mt-2.5 text-[14px] font-extrabold text-ink text-center leading-tight">{domain.full}</div>
    </div>
  );
}

export function SubjectPanel({ subject, real, agenda, progressError = false, unplaced = false }) {
  const { user, handleAPIResponse, viewAs } = useAuth();

  // Placement checks — every domain, both subjects (reading added
  // 2026-08-27). Each domain has its own adaptive check, so an unplaced card
  // is a live entry rather than a dead square waiting on the diagnostic. Read
  // once, and only when something is actually unplaced; a failed read leaves
  // the cards on their default "Find your level" copy rather than blanking
  // them. Admin view mode skips the read (the placement API only knows the
  // signed-in account): the cards keep their default copy.
  const [walks, setWalks] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!user || !real || viewAs) return undefined;
    const anyUnplaced = (DOMAINS[subject] || []).some((d) => real.domains?.[d.key]?.band == null);
    if (!anyUnplaced) return undefined;
    fetchAllWalks({ user, handleAPIResponse })
      .then((w) => { if (alive) setWalks(w); })
      .catch(() => { /* cards keep their default copy */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, user, real]);

  // Signed in: seed empty (nulls), never the demo mock's levels — the home
  // page holds the whole screen on the cube loader until the real data lands,
  // and a failed fetch leaves the honest not-placed empty states.
  const [levels, setLevels] = useState(() =>
    Object.fromEntries(DOMAINS[subject].map((d) => [d.key, user ? null : progressOf(d.key, unplaced).level]))
  );
  const [ready, setReady] = useState(() => (user ? {} : READY_DEFAULT));
  const [pcts, setPcts] = useState(() =>
    Object.fromEntries(DOMAINS[subject].map((d) => {
      if (user) return [d.key, 0];
      const p = progressOf(d.key, unplaced);
      const total = p.level != null ? partsFor(d.key, p.level).length : 1;
      return [d.key, p.level != null ? p.part / total : 0];
    }))
  );

  // REAL engine state (passed down from the home page's getMyProgress): the
  // moment it exists it replaces the demo mock — EVEN when the student is
  // placed nowhere yet (Sandy 2026-07-27: gating this on `placed` dropped
  // never-placed real accounts into demo mode, fake Level-up included).
  useEffect(() => {
    if (!real) return;
    const lv = {};
    const pc = {};
    for (const d of DOMAINS[subject]) {
      const rd = real.domains[d.key];
      lv[d.key] = rd?.band ?? null;
      const up = rd?.unitProgress || {};
      const done = Math.min(4, up.a1 || 0) + Math.min(4, up.a2 || 0) + Math.min(1, up.a3 || 0) + Math.min(1, up.a4 || 0);
      pc[d.key] = rd?.band != null ? done / 10 : 0;
    }
    setLevels(lv);
    setPcts(pc);
    // Real level-ups happen inside the engine (mastery checks move the band
    // server-side) — the demo "Level up" button never shows on real data.
    setReady(Object.fromEntries(DOMAINS[subject].map((d) => [d.key, false])));
    const placed = Object.values(lv).filter((l) => l != null);
    if (placed.length) {
      const m = Math.min(...placed);
      const [lo, hi] = bandRange(subject, m);
      setRange([lo, hi]);
      setMid(Math.round((lo + hi) / 2 / 10) * 10);
    } else {
      setRange(null);
      setMid(null);
    }
    // REAL level-up celebration: compare each domain's band with the last band
    // this account saw here (per-user localStorage). A higher band fires the
    // same score celebration the demo uses — no mock state touched, no manual
    // Level-up button. First visit (nothing stored) just stores silently.
    const uid = user?.username || user?.sub || 'me';
    const storeKey = `sdv3.lastSeenBand.${uid}`;
    let seen = {};
    try { seen = JSON.parse(window.localStorage.getItem(storeKey)) || {}; } catch { seen = {}; }
    let leveled = null;
    for (const d of DOMAINS[subject]) {
      const nb = lv[d.key];
      if (leveled == null && nb != null && seen[d.key] != null && nb > seen[d.key]) leveled = d.key;
    }
    const remembered = { ...seen };
    for (const [k, v] of Object.entries(real.domains || {})) {
      if (v?.band != null) remembered[k] = v.band;
    }
    try { window.localStorage.setItem(storeKey, JSON.stringify(remembered)); } catch { /* storage off */ }
    if (leveled && placed.length) {
      const m2 = Math.min(...placed);
      const [lo2, hi2] = bandRange(subject, m2);
      const newMid = Math.round((lo2 + hi2) / 2 / 10) * 10;
      const pts = DOMAIN_POINTS[leveled] || 15;
      setAnim((a) => (a ? a : { from: newMid - pts, pts, to: newMid, phase: 'count' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [real, subject]);

  const placedLevels = Object.values(levels).filter((l) => l != null);
  const minLevel = placedLevels.length ? Math.min(...placedLevels) : null;

  // Subjects have NO level of their own (removed 2026-07-14) — just the score
  // estimate. The range comes from the same band mapping the other pages use;
  // domain level-ups still nudge the median up.
  const [range, setRange] = useState(() => (minLevel != null ? bandRange(subject, minLevel) : null));
  const [mid, setMid] = useState(() => {
    if (minLevel == null) return null;
    const [lo, hi] = bandRange(subject, minLevel);
    return Math.round((lo + hi) / 2 / 10) * 10;
  });

  // Level-up score celebration, a four-phase machine:
  // count (range fades, "old + pts =" with the new score ticking up in the
  // brand rainbow) -> hold (equation fades, the gradient number slides left
  // into the score's spot and sits there) -> fade (gradient crossfades back
  // to ink, range returns) -> null.
  const [anim, setAnim] = useState(null);
  useEffect(() => {
    if (!anim) return undefined;
    if (anim.phase === 'hold') {
      const t = setTimeout(() => {
        setMid(anim.to);
        const lvls = Object.values(levels).filter((l) => l != null);
        if (lvls.length) setRange(bandRange(subject, Math.min(...lvls)));
        setAnim({ ...anim, phase: 'fade' });
      }, 2000);
      return () => clearTimeout(t);
    }
    if (anim.phase === 'fade') {
      const t = setTimeout(() => setAnim(null), 750);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [anim, levels]);

  const levelUpDomain = (key) => {
    if (anim) return; // one celebration at a time
    setLevels((ls) => ({ ...ls, [key]: Math.min(ls[key] + 1, MAX_LEVEL) }));
    setReady((r) => ({ ...r, [key]: false }));
    setPcts((p) => ({ ...p, [key]: 0 }));
    const pts = DOMAIN_POINTS[key] || 15;
    if (mid != null) setAnim({ from: mid, pts, to: mid + pts, phase: 'count' });
  };

  // Path order: the teacher's class unit is pinned to the front when it
  // matches this subject; the rest sort by lowest level first, tie broken by
  // score weight. Recomputed from live levels so a level-up updates the plan.
  // Once the signed-in agenda loads, the pin is the REAL class-unit domain
  // (or no pin at all when the class isn't working in this subject) — the
  // mock's STUDENT.active only backs signed-out demos.
  const unit = STUDENT.active;
  const agendaLoaded = !!agenda?.loaded;
  // ALL class units in this subject (Sandy 2026-08-09): a student in two
  // classes can have two live units at once — every one gets pinned to the
  // front (earliest next due first) and chipped with ITS OWN due date. If two
  // classes run the SAME domain, the earlier next-due owns the chip.
  const nextSlotOf = (a) => (a?.slots || []).find(isOpenAssignmentSlot) || null;
  const nextDueMsOf = (a) => {
    const s = nextSlotOf(a);
    return s?.due ? dueDateOf(s.due).getTime() : Infinity;
  };
  const realAsgs = agendaLoaded
    ? (agenda.assignments || [])
        .filter((a) => (a.subject === 'math' ? 'math' : 'reading') === subject
          && DOMAINS[subject].some((d) => d.key === a.domainKey))
        .sort((a, b) => nextDueMsOf(a) - nextDueMsOf(b))
    : [];
  const asgByDomain = new Map();
  for (const a of realAsgs) if (!asgByDomain.has(a.domainKey)) asgByDomain.set(a.domainKey, a);
  const realAsg = realAsgs[0] || null; // earliest-due unit = the primary stop
  const pinnedKeys = agendaLoaded
    ? [...asgByDomain.keys()]
    : (!unplaced && unit.subject === subject ? [unit.domain] : []);
  const pinnedKey = pinnedKeys[0] || null;
  const doms = DOMAINS[subject];
  const orderedDomains = unplaced ? doms : (() => {
    const rest = doms
      .filter((d) => !pinnedKeys.includes(d.key))
      .sort((a, b) => (levels[a.key] ?? 99) - (levels[b.key] ?? 99)
        || (DOMAIN_POINTS[b.key] || 15) - (DOMAIN_POINTS[a.key] || 15));
    const pinned = pinnedKeys.map((k) => doms.find((d) => d.key === k)).filter(Boolean);
    return [...pinned, ...rest];
  })();

  // Buffy's one "because" line: always answers why the first stop is first.
  // Reasons are rule codes with real numbers, so the same logic can feed the
  // teacher dashboard in identical language.
  const focus = orderedDomains[0];
  const weeksLeft = unit.total - unit.week;
  // Real pin: the "what's due" truth comes from the assignment's own slots
  // (first not-done, not-N/A slot — its label is the teacher's real sequence).
  const nextSlot = nextSlotOf(realAsg);
  // The slot's due may be date-only or a full ISO datetime (2026-08-07):
  // dueDateOf renders both correctly, shortDue adds the time when one exists.
  const slotDate = nextSlot ? dueDateOf(nextSlot.due) : null;
  const due = agendaLoaded
    ? (nextSlot ? {
        action: `finish your ${String(nextSlot.label || 'assignment').toLowerCase()}`,
        due: slotDate,
        dueLong: shortDue(nextSlot.due),
        dueDay: slotDate.toLocaleDateString('en-US', { weekday: 'long' }),
      } : null)
    : (pinnedKey ? unitDue() : null);
  let buffyLine;
  if (unplaced) {
    buffyLine = "Take the diagnostic and I'll map out your path.";
  } else if (user && !real && progressError) {
    // Signed in but the progress fetch failed: say so, no mock numbers.
    buffyLine = "We couldn't load your progress right now. Refresh to try again.";
  } else if (real && minLevel == null) {
    // Real account, placed nowhere yet: the first session self-places them.
    buffyLine = 'Pick a domain and jump in — your first session finds your level.';
  } else if (ready[focus.key]) {
    buffyLine = `You passed the Level ${levels[focus.key]} check in ${focus.full}! Hit Level up to bank your points`
      + (due ? `, then ${due.action.toLowerCase()} by ${due.dueDay}.` : pinnedKey ? `: your class is here for ${weeksLeft} more ${weeksLeft === 1 ? 'week' : 'weeks'}.` : '.');
  } else if (pinnedKey && agendaLoaded && pinnedKeys.length > 1) {
    // TWO live class units (two classes) — name both with their own dues,
    // earliest first (Sandy 2026-08-09).
    const second = doms.find((dd) => dd.key === pinnedKeys[1]);
    const sSlot = nextSlotOf(asgByDomain.get(pinnedKeys[1]));
    const sDay = sSlot?.due ? dueDateOf(sSlot.due).toLocaleDateString('en-US', { weekday: 'long' }) : null;
    buffyLine = `Two of your classes have live units: ${focus.full}`
      + (due ? ` (${due.action} by ${due.dueDay})` : '')
      + ` and ${second.full}`
      + (sDay ? ` (finish your ${String(sSlot.label || 'assignment').toLowerCase()} by ${sDay})` : '')
      + '. Start with whichever is due first.';
  } else if (pinnedKey && agendaLoaded) {
    // Real class unit: the line always states the real next slot when there is
    // one; a finished unit just says so, never mock pacing weeks.
    buffyLine = `Your class is working on ${focus.full}`
      + (due ? `: ${due.action} by ${due.dueDay}.` : (realAsg?.slots || []).some(isUnknownAssignmentSlot)
        ? ', but some checkpoint results are unavailable.' : ', and you have finished every due part of the unit.')
      + (orderedDomains[1] ? ` After the unit, ${orderedDomains[1].full} is next: it's your lowest level.` : '');
  } else if (pinnedKey) {
    buffyLine = `Your class is working on ${focus.full}`
      + (due ? `: ${due.action.toLowerCase()} by ${due.dueDay}.` : ` for the next ${weeksLeft} ${weeksLeft === 1 ? 'week' : 'weeks'}.`)
      + ` After the unit, ${orderedDomains[1].full} is next: it's your lowest level.`;
  } else {
    buffyLine = `I put ${focus.full} first because it's your lowest level. Every level you finish there adds about ${DOMAIN_POINTS[focus.key] || 15} points to your score.`;
  }

  return (
    <div>
      {/* The per-subject predicted score lockup and Buffy's line were both cut
          (Sandy 2026-08-23): the subject switch below carries the identity now,
          and the total predicted score already lives in the hero above. NOTE: the
          level-up score celebration (the four-phase `anim` machine) used to play
          inside that lockup and now has no stage. */}
      {/* the path: four stops connected left to right in working order,
          straight on the white page */}
      <div className="relative px-0 sm:px-8 pt-2 pb-2 mt-1" data-tour="path">
        {/* connector line runs behind the cards at card-center height —
            hidden pre-diagnostic (Sandy 2026-08-12: with every card grayed
            out there is no path yet, and it read as a stray gray bar). Phones
            stack the path 2x2, where a horizontal connector reads as noise. */}
        {!unplaced && (
          <div className="hidden sm:block absolute left-[13%] right-[13%] top-[136px] h-[3px] bg-neutral-200" />
        )}
        {/* Phones: 2x2 grid - four rigid columns collided at 375px and the
            domain names overlapped into an unreadable pile (Sandy 2026-08-18). */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-8 sm:grid-cols-4 sm:gap-x-8 sm:gap-y-0">
          {orderedDomains.map((d, i) => (
            <PathCard
              key={d.key}
              subject={subject}
              domain={d}
              level={levels[d.key]}
              ready={!!ready[d.key] && levels[d.key] != null}
              pct={pcts[d.key]}
              // A REAL class unit is active even pre-diagnostic (Sandy
              // 2026-08-30, same rule as the due chip): the placement walk is
              // its assignment 1. Only the mock "first card" fallback stays
              // suppressed in the unplaced presentation.
              active={agendaLoaded ? asgByDomain.has(d.key) : (!unplaced && i === 0)}
              dueLabel={(() => {
                // Every class-unit domain gets its own chip (two classes can
                // run two units at once — Sandy 2026-08-09); the class name
                // disambiguates when there is more than one.
                // A REAL class unit chips its card even pre-diagnostic (Sandy
                // 2026-08-30): the placement walk is the unit's assignment 1
                // now, so an unplaced student's "Find your level" card carries
                // slot 1's real due date. Only the mock fallback below stays
                // suppressed in the unplaced presentation.
                if (agendaLoaded) {
                  const a = asgByDomain.get(d.key);
                  if (!a) return '';
                  // Just the due date (Sandy 2026-08-12) — no "Class unit"
                  // preamble. The class name still disambiguates two units.
                  const cname = pinnedKeys.length > 1 && a.className ? ` (${a.className})` : '';
                  const s = nextSlotOf(a);
                  if (s?.due) return `${dueEndMs(s.due) < Date.now() ? 'Overdue · ' : 'Due '}${shortDue(s.due)}${cname}`;
                  return pinnedKeys.length > 1 && a.className ? a.className : '';
                }
                if (unplaced) return '';
                return i === 0 && due ? `Due ${due.dueLong}` : '';
              })()}
              // Past its deadline: the chip goes red (Sandy 2026-09-02).
              dueOverdue={(() => {
                if (!agendaLoaded) return false;
                const a = asgByDomain.get(d.key);
                const s = a ? nextSlotOf(a) : null;
                return !!s?.due && dueEndMs(s.due) < Date.now();
              })()}
              onLevelUp={levelUpDomain}
              startable={!!real && !unplaced}
              // Unplaced (math or reading): this card starts the domain's own
              // placement check. Signed-out demos stay on the diagnostic
              // story, since a walk has to record against a real account.
              placement={!!user && hasPlacement(d.key) && levels[d.key] == null
                ? {
                  status: walks?.[d.key]?.status || 'none',
                  placedBand: walks?.[d.key]?.result?.placedBand ?? null,
                }
                : null}
            />
          ))}
        </div>
      </div>

    </div>
  );
}

// The old standalone subject page: /student-dashboard/:subject now lives as a
// TAB on the home page (Sandy 2026-08-12). Every existing link redirects into
// it; the domain pages (/student-dashboard/:subject/:domainKey) are untouched.
export default function StudentSubjectPage() {
  const { subject } = useParams();
  return <Navigate to={DOMAINS[subject] ? `/student-dashboard?tab=${subject}` : '/student-dashboard'} replace />;
}
